import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { useNavigate, useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import ViewOrUpdateApiBody from "./viewOrUpdateApiBody";

// HTTP methods allowed for lab controller APIs
const HTTP_METHODS = ["POST", "GET", "PUT", "DELETE", "PATCH", "Webhook"];

const EMPTY_ROW = { apiName: "", httpMethod: "POST", contextUrl: "" };

function ApiUrlSettings() {
  const { orgcode } = useParams();
  const navigate = useNavigate();

  const [loader, setLoader] = useState(false);
  const [labCorporateDetails, setLabCorporateDetails] = useState({});
  const [isApiUrlSettingsEnabled, setIsApiUrlSettingsEnabled] = useState(false);

  // Base auth settings (kept from original)
  const [authMethods, setAuthMethods] = useState([]);
  const [authMethod, setAuthMethod] = useState("");
  const [authFields, setAuthFields] = useState({});
  const [authValues, setAuthValues] = useState([]);
  const [baseApiUrl, setBaseApiUrl] = useState("");
  const [authSettingsId, setAuthSettingsId] = useState(null);

  // Custom-Header auth
  const CUSTOM_HEADER_TYPE = "CUSTOM_HEADER";
  // Normalise-compare so "Custom Header", "CUSTOM_HEADER", "custom header" all match
  const isCustomHeaderType = (str) =>
    (str || "").toLowerCase().replace(/[\s_-]+/g, "") === "customheader";
  const EMPTY_HEADER = { headerName: "", headerValue: "" };
  const [customHeaders, setCustomHeaders] = useState([{ ...EMPTY_HEADER }]);
  const [customHeadersApiSettingsId, setCustomHeadersApiSettingsId] = useState(null);

  // Helper: is the currently selected auth method a CUSTOM_HEADER type?
  // Case-insensitive + normalised so it matches "Custom Header", "CUSTOM_HEADER", "custom header", etc.
  const isCustomHeader = () => {
    const m = authMethods.find((x) => String(x.id) === String(authMethod));
    if (!m) return false;
    const normalised = (m.authenticationType || "")
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
    return normalised === "customheader";
  };

  const getMethodBadgeStyle = (method) => {
    const m = (method || "").toUpperCase();
    switch (m) {
      case "GET":
        return { bg: "rgba(34, 197, 94, 0.15)", color: "#4ade80", border: "1px solid rgba(34, 197, 94, 0.3)" };
      case "POST":
        return { bg: "rgba(56, 189, 248, 0.15)", color: "#38bdf8", border: "1px solid rgba(56, 189, 248, 0.3)" };
      case "PUT":
        return { bg: "rgba(245, 158, 11, 0.15)", color: "#fbbf24", border: "1px solid rgba(245, 158, 11, 0.3)" };
      case "DELETE":
        return { bg: "rgba(239, 68, 68, 0.15)", color: "#f87171", border: "1px solid rgba(239, 68, 68, 0.3)" };
      case "PATCH":
        return { bg: "rgba(168, 85, 247, 0.15)", color: "#c084fc", border: "1px solid rgba(168, 85, 247, 0.3)" };
      default:
        return { bg: "rgba(148, 163, 184, 0.15)", color: "#cbd5e1", border: "1px solid rgba(148, 163, 184, 0.3)" };
    }
  };

  // Dynamic API rows
  const [apiRows, setApiRows] = useState([]);

  // Add / Edit row modal
  const [isRowModalOpen, setIsRowModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // null = add new, object = edit
  const [rowForm, setRowForm] = useState({ ...EMPTY_ROW });
  const [addRows, setAddRows] = useState([{ ...EMPTY_ROW }]);

  // Body modal
  const [isBodyModalOpen, setIsBodyModalOpen] = useState(false);
  const [bodyModalApiId, setBodyModalApiId] = useState(null);
  const [bodyModalApiName, setBodyModalApiName] = useState("");

  // Delete confirm modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ────────────────────────────── DATA FETCHING ──────────────────────────────

  const handleError = (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("An error occurred.");
      }
    } else {
      toast.error(error.message || "An error occurred.");
    }
  };

  const getData = async () => {
    setLoader(true);
    const token = JSON.parse(localStorage.getItem("data")).token;
    try {
      const res = await axios.get(`/api/lab-corporate/orgcode=${orgcode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabCorporateDetails(res.data);
      setIsApiUrlSettingsEnabled(!!res.data.apiUrlSettings);
    } catch (error) {
      handleError(error);
    } finally {
      setLoader(false);
    }
  };

  const getAuthMethods = async () => {
    const token = JSON.parse(localStorage.getItem("data")).token;
    try {
      const res = await axios.get(`/api/authentication-methods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuthMethods(res.data);
    } catch (error) {
      handleError(error);
    }
  };

  const fetchAuthSettings = async (labCorporateId) => {
    const token = JSON.parse(localStorage.getItem("data")).token;
    try {
      const res = await axios.get(
        `/api/get-api-url/labcorporateid=${labCorporateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.apiUrlSettings) {
        const s = res.data.apiUrlSettings;
        setBaseApiUrl(s.baseApiUrl || "");
        setAuthMethod(s.authMethodId || "");
        setAuthSettingsId(s.id || null);

        if (s.authMethodId) {
          // Fetch auth-method details to determine type
          const allMethods = await axios.get(`/api/authentication-methods`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const methods = allMethods.data || [];
          const selectedMethod = methods.find(
            (x) => String(x.id) === String(s.authMethodId)
          );

          if (isCustomHeaderType(selectedMethod?.authenticationType)) {
            // Load custom headers from the dedicated table
            await fetchCustomHeaders(s.id || null, token);
          } else {
            const authRes = await axios.get(
              `/api/auth-required-values/authtypeid=${s.authMethodId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setAuthFields(authRes.data || {});
            setAuthValues(res.data.authValues || []);
          }
        }
      }
    } catch (error) {
      // not found is ok
      if (error.response?.status !== 404) handleError(error);
    }
  };

  const fetchCustomHeaders = async (apiSettingsId, tokenOverride) => {
    const token = tokenOverride || JSON.parse(localStorage.getItem("data")).token;
    if (!apiSettingsId) return;
    try {
      const res = await axios.get(
        `/api/lab-api-headers/apisettingsid=${apiSettingsId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const rows = res.data || [];
      setCustomHeaders(rows.length > 0 ? rows : [{ ...EMPTY_HEADER }]);
    } catch (error) {
      if (error.response?.status !== 404) handleError(error);
      else setCustomHeaders([{ ...EMPTY_HEADER }]);
    }
  };

  const fetchApiRows = async (labCorporateId) => {
    const token = JSON.parse(localStorage.getItem("data")).token;
    try {
      const res = await axios.get(
        `/api/lab-controller-apis/labcorporateid=${labCorporateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApiRows(res.data || []);
    } catch (error) {
      if (error.response?.status !== 404) handleError(error);
    }
  };

  useEffect(() => {
    getData();
    getAuthMethods();
  }, []);

  useEffect(() => {
    if (labCorporateDetails.id && isApiUrlSettingsEnabled) {
      fetchAuthSettings(labCorporateDetails.id);
      fetchApiRows(labCorporateDetails.id);
    }
  }, [labCorporateDetails, isApiUrlSettingsEnabled]);

  // ────────────────────────────── SWITCH ──────────────────────────────

  const handleSwitchChange = async (e) => {
    const isChecked = e.target.checked;
    setIsApiUrlSettingsEnabled(isChecked);
    const token = JSON.parse(localStorage.getItem("data")).token;
    try {
      await axios.post(
        `/api/lab-corporate/update`,
        { id: labCorporateDetails.id, apiUrlSettings: isChecked },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Updated successfully.");
      getData();
    } catch (error) {
      handleError(error);
    }
  };

  // ────────────────────────────── AUTH SETTINGS ──────────────────────────────

  const handleAuthMethodChange = async (e) => {
    const selected = e.target.value;
    setAuthMethod(selected);
    setAuthValues([]);
    setAuthFields({});
    setCustomHeaders([{ ...EMPTY_HEADER }]);
    if (!selected) return;

    const selectedMethod = authMethods.find((x) => String(x.id) === String(selected));
    if (isCustomHeaderType(selectedMethod?.authenticationType)) {
      // Custom header: load existing headers if we already have a settings id
      if (authSettingsId) {
        await fetchCustomHeaders(authSettingsId);
      }
      return;
    }

    const token = JSON.parse(localStorage.getItem("data")).token;
    try {
      const res = await axios.get(
        `/api/auth-required-values/authtypeid=${selected}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAuthFields(res.data || {});
    } catch (error) {
      handleError(error);
    }
  };

  const handleAuthValueChange = (id, newValue) => {
    setAuthValues((prev) =>
      prev
        .map((a) =>
          a.requiredValuesId === id ? { ...a, authValues: newValue } : a
        )
        .concat(
          !prev.find((a) => a.requiredValuesId === id)
            ? [{ requiredValuesId: id, authValues: newValue }]
            : []
        )
    );
  };

  // ── Custom Header rows ──
  const handleCustomHeaderChange = (index, field, value) => {
    setCustomHeaders((prev) =>
      prev.map((h, i) => (i === index ? { ...h, [field]: value } : h))
    );
  };

  const handleAddCustomHeaderRow = () => {
    setCustomHeaders((prev) => [...prev, { ...EMPTY_HEADER }]);
  };

  const handleRemoveCustomHeaderRow = (index) => {
    setCustomHeaders((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveAuthSettings = async (e) => {
    e.preventDefault();
    const token = JSON.parse(localStorage.getItem("data")).token;

    // 1. Save / update auth settings record (same as before)
    const payload = {
      apiUrlSettings: {
        ...(authSettingsId ? { id: authSettingsId } : {}),
        baseApiUrl,
        authMethodId: authMethod,
        labCorporateId: labCorporateDetails.id,
      },
      authValues: isCustomHeader() ? [] : authValues,
    };
    try {
      const settingsRes = await axios.post(
        `/api/apiUrlSettings/createOrUpdate`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const savedSettingsId =
        settingsRes.data?.apiUrlSettings?.id || authSettingsId;

      // 2. If CUSTOM_HEADER, persist the header rows
      if (isCustomHeader() && savedSettingsId) {
        const validHeaders = customHeaders.filter(
          (h) => h.headerName.trim() !== ""
        );
        if (validHeaders.length === 0) {
          toast.error("Please add at least one header name.");
          return;
        }
        await axios.post(
          `/api/lab-api-headers/createOrUpdate`,
          {
            apiSettingsId: savedSettingsId,
            headers: validHeaders.map((h) => ({
              ...(h.id ? { id: h.id } : {}),
              apiId: savedSettingsId,
              headerName: h.headerName.trim(),
              headerValue: h.headerValue,
            })),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success("Auth settings saved successfully.");
      fetchAuthSettings(labCorporateDetails.id);
    } catch (error) {
      handleError(error);
    }
  };

  // ────────────────────────────── ROW MODAL ──────────────────────────────

  const openAddRowModal = () => {
    setEditingRow(null);
    setAddRows([{ ...EMPTY_ROW }]);
    setIsRowModalOpen(true);
  };

  const openEditRowModal = (row) => {
    setEditingRow(row);
    setRowForm({ apiName: row.apiName, httpMethod: row.httpMethod, contextUrl: row.contextUrl });
    setIsRowModalOpen(true);
  };

  const handleRowFormChange = (e) => {
    const { name, value } = e.target;
    setRowForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRowItem = () => {
    setAddRows((prev) => [...prev, { ...EMPTY_ROW }]);
  };

  const handleRemoveRowItem = (index) => {
    setAddRows((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleAddRowChange = (index, field, value) => {
    setAddRows((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  };

  const handleSaveRow = async () => {
    const token = JSON.parse(localStorage.getItem("data")).token;
    try {
      if (editingRow) {
        if (!rowForm.apiName.trim()) {
          toast.error("API Name is required.");
          return;
        }
        if (!rowForm.contextUrl.trim()) {
          toast.error("Context / Endpoint URL is required.");
          return;
        }
        await axios.post(
          `/api/lab-controller-apis/update`,
          { ...editingRow, ...rowForm },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("API updated.");
      } else {
        // Validation for addRows
        for (let i = 0; i < addRows.length; i++) {
          const r = addRows[i];
          if (!r.apiName.trim()) {
            toast.error(`Row #${i + 1}: API Name is required.`);
            return;
          }
          if (!r.contextUrl.trim()) {
            toast.error(`Row #${i + 1}: Context / Endpoint URL is required.`);
            return;
          }
        }

        setLoader(true);
        await Promise.all(
          addRows.map((r) =>
            axios.post(
              `/api/lab-controller-apis/create`,
              { ...r, labCorporateId: labCorporateDetails.id, isActive: true },
              { headers: { Authorization: `Bearer ${token}` } }
            )
          )
        );
        toast.success(
          addRows.length > 1
            ? `${addRows.length} APIs created successfully.`
            : "API added successfully."
        );
      }
      setIsRowModalOpen(false);
      fetchApiRows(labCorporateDetails.id);
    } catch (error) {
      handleError(error);
    } finally {
      setLoader(false);
    }
  };

  // ────────────────────────────── DELETE ──────────────────────────────

  const openDeleteModal = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const token = JSON.parse(localStorage.getItem("data")).token;
    try {
      await axios.delete(`/api/lab-controller-apis`, {
        headers: { Authorization: `Bearer ${token}` },
        data: deleteTarget,
      });
      toast.success("API deleted.");
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
      fetchApiRows(labCorporateDetails.id);
    } catch (error) {
      handleError(error);
    }
  };

  // ────────────────────────────── BODY MODAL ──────────────────────────────

  const openBodyModal = (row) => {
    setBodyModalApiId(row.id);
    setBodyModalApiName(row.apiName);
    setIsBodyModalOpen(true);
  };

  // ────────────────────────────── RENDER ──────────────────────────────

  return (
    <div className="container mt-4">
      {loader && <div className="loading"></div>}

      {/* Enable / Disable toggle */}
      <div className="form-check form-switch mb-4">
        <input
          type="checkbox"
          className="form-check-input"
          id="apiUrlSettingsSwitch"
          checked={isApiUrlSettingsEnabled}
          onChange={handleSwitchChange}
        />
        <label className="form-check-label fw-semibold" htmlFor="apiUrlSettingsSwitch">
          API URL Settings
        </label>
      </div>

      {isApiUrlSettingsEnabled && (
        <>
          {/* ── Auth / Base URL section ── */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">lock</i>
                Authentication &amp; Base URL
              </h3>
            </div>
            <div className="hm-card-body">
              <form onSubmit={handleSaveAuthSettings}>
                <table className="table table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td style={{ width: "220px" }}>
                        <label htmlFor="baseApiUrl">Base API URL:</label>
                      </td>
                      <td>
                        <input
                          type="text"
                          id="baseApiUrl"
                          className="form-control"
                          value={baseApiUrl}
                          onChange={(e) => setBaseApiUrl(e.target.value)}
                          placeholder="Enter API URL"
                          style={{ width: "350px" }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <label htmlFor="authMethodId">Authentication Method:</label>
                      </td>
                      <td>
                        <select
                          id="authMethodId"
                          className="form-select"
                          value={authMethod}
                          onChange={handleAuthMethodChange}
                          style={{ width: "350px" }}
                        >
                          <option value="">Select Authentication Method</option>
                          {authMethods.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.authenticationType}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>

                    {authMethod && (
                      <>
                        {isCustomHeader() ? (
                          /* ── CUSTOM HEADER table ── */
                          <tr>
                            <td colSpan="2" style={{ paddingTop: "12px", paddingBottom: "4px" }}>
                              {/* Header label row */}
                              <div style={{ maxWidth: "560px" }}>
                                <table
                                  className="table table-sm mb-0"
                                  style={{ tableLayout: "fixed", width: "100%" }}
                                >
                                  <colgroup>
                                    <col style={{ width: "45%" }} />
                                    <col style={{ width: "45%" }} />
                                    <col style={{ width: "10%" }} />
                                  </colgroup>
                                  <thead>
                                    <tr>
                                      <th style={{ paddingLeft: "8px", fontSize: "11px", letterSpacing: "0.08em" }}>KEY</th>
                                      <th style={{ fontSize: "11px", letterSpacing: "0.08em" }}>VALUE</th>
                                      <th />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {customHeaders.map((h, idx) => (
                                      <tr key={idx}>
                                        <td style={{ padding: "4px 6px 4px 8px", verticalAlign: "middle" }}>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm border-0 shadow-none"
                                            placeholder="e.g. UserId"
                                            value={h.headerName}
                                            onChange={(e) =>
                                              handleCustomHeaderChange(idx, "headerName", e.target.value)
                                            }
                                            style={{ background: "transparent" }}
                                          />
                                        </td>
                                        <td style={{ padding: "4px 6px", verticalAlign: "middle" }}>
                                          <input
                                            type="text"
                                            className="form-control form-control-sm border-0 shadow-none"
                                            placeholder="Value"
                                            value={h.headerValue}
                                            onChange={(e) =>
                                              handleCustomHeaderChange(idx, "headerValue", e.target.value)
                                            }
                                            style={{ background: "transparent" }}
                                          />
                                        </td>
                                        <td style={{ verticalAlign: "middle", textAlign: "center", padding: "4px" }}>
                                          {customHeaders.length > 1 && (
                                            <button
                                              type="button"
                                              className="btn btn-link text-danger p-0"
                                              onClick={() => handleRemoveCustomHeaderRow(idx)}
                                              title="Remove header"
                                            >
                                              <i className="material-icons" style={{ fontSize: "16px", verticalAlign: "middle" }}>
                                                close
                                              </i>
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                <div className="mt-2 d-flex align-items-center gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm rounded-pill"
                                    onClick={handleAddCustomHeaderRow}
                                  >
                                    <i className="material-icons" style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "4px" }}>
                                      add
                                    </i>
                                    Add Header
                                  </button>
                                  <span className="text-muted small">
                                    Add any number of key-value header pairs
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : Object.keys(authFields).length > 0 ? (
                          Object.entries(authFields).map(([key, field]) => (
                            <tr key={field.requiredValuesId || key}>
                              <td>
                                <label>{field.requiredValues}:</label>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control"
                                  style={{ width: "350px" }}
                                  placeholder={field.placeholder}
                                  value={
                                    authValues.find(
                                      (a) => a.requiredValuesId === field.id
                                    )?.authValues || ""
                                  }
                                  onChange={(e) =>
                                    handleAuthValueChange(field.id, e.target.value)
                                  }
                                />
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="2" className="text-muted small">
                              No additional fields required for this auth method.
                            </td>
                          </tr>
                        )}
                      </>
                    )}

                    <tr>
                      <td colSpan="2">
                        <button type="submit" className="btn btn-primary rounded-pill px-4">
                          Save Auth Settings
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </form>
            </div>
          </div>

          {/* ── Dynamic API Endpoints table ── */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">api</i>
                API Endpoints
              </h3>
              <button
                className="btn btn-primary rounded-pill px-4 btn-sm"
                onClick={openAddRowModal}
              >
                <i className="material-icons" style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "4px" }}>
                  add
                </i>
                Add API
              </button>
            </div>
            <div className="hm-card-body p-0">
              {apiRows.length === 0 ? (
                <div className="hm-empty-state">
                  <i className="material-icons">api</i>
                  <p>No API endpoints configured yet. Click "Add API" to get started.</p>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="hm-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>API Name</th>
                        <th>HTTP Method</th>
                        <th>Context / Endpoint URL</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiRows.map((row, index) => {
                        const badge = getMethodBadgeStyle(row.httpMethod);
                        return (
                          <tr key={row.id}>
                            <td style={{ verticalAlign: "middle" }}>{index + 1}</td>
                            <td style={{ verticalAlign: "middle" }}>
                              <strong style={{ fontSize: "14px" }}>{row.apiName}</strong>
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              <span
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  letterSpacing: "0.5px",
                                  backgroundColor: badge.bg,
                                  color: badge.color,
                                  border: badge.border,
                                  display: "inline-block",
                                  textTransform: "uppercase"
                                }}
                              >
                                {row.httpMethod}
                              </span>
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              <span
                                style={{
                                  fontFamily: "'Fira Code', 'Consolas', 'Courier New', monospace",
                                  fontSize: "13px",
                                  color: "#38bdf8",
                                  fontWeight: "500",
                                  letterSpacing: "0.2px",
                                  wordBreak: "break-all"
                                }}
                              >
                                {row.contextUrl}
                              </span>
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <button
                                  className="btn btn-sm rounded-pill px-3"
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#38bdf8",
                                    border: "1px solid rgba(56, 189, 248, 0.4)",
                                    background: "rgba(56, 189, 248, 0.08)",
                                    transition: "all 0.2s"
                                  }}
                                  onClick={() => openBodyModal(row)}
                                  title="View / Update Body"
                                >
                                  <i className="material-icons" style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "3px" }}>
                                    code
                                  </i>
                                  Body
                                </button>
                                <button
                                  className="btn btn-sm rounded-pill px-3"
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#60a5fa",
                                    border: "1px solid rgba(96, 165, 250, 0.4)",
                                    background: "rgba(96, 165, 250, 0.08)",
                                    transition: "all 0.2s"
                                  }}
                                  onClick={() => openEditRowModal(row)}
                                  title="Edit"
                                >
                                  <i className="material-icons" style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "3px" }}>
                                    edit
                                  </i>
                                  Edit
                                </button>
                                <button
                                  className="btn btn-sm rounded-pill px-3"
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#f87171",
                                    border: "1px solid rgba(248, 113, 113, 0.4)",
                                    background: "rgba(248, 113, 113, 0.08)",
                                    transition: "all 0.2s"
                                  }}
                                  onClick={() => openDeleteModal(row)}
                                  title="Delete"
                                >
                                  <i className="material-icons" style={{ fontSize: "14px", verticalAlign: "middle", marginRight: "3px" }}>
                                    delete
                                  </i>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Add / Edit API Row Modal ── */}
      <Modal show={isRowModalOpen} onHide={() => setIsRowModalOpen(false)} centered size={editingRow ? "md" : "lg"}>
        <Modal.Header closeButton>
          <Modal.Title>{editingRow ? "Edit API Endpoint" : "Add API Endpoints"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingRow ? (
            <>
              <div className="mb-3">
                <label className="form-label fw-semibold">API Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  name="apiName"
                  value={rowForm.apiName}
                  onChange={handleRowFormChange}
                  placeholder="e.g. OrderBooking, GetOrderStatus"
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">HTTP Method</label>
                <select
                  className="form-select"
                  name="httpMethod"
                  value={rowForm.httpMethod}
                  onChange={handleRowFormChange}
                >
                  {HTTP_METHODS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Context / Endpoint URL <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  name="contextUrl"
                  value={rowForm.contextUrl}
                  onChange={handleRowFormChange}
                  placeholder="e.g. /api/IntegrationAPI/OrderBooking"
                />
                <div className="form-text">
                  Enter the path context (e.g. <code style={{ color: "#38bdf8", backgroundColor: "rgba(56, 189, 248, 0.12)", padding: "2px 6px", borderRadius: "4px" }}>/api/IntegrationAPI/OrderBooking</code>) or the full URL if different from base.
                </div>
              </div>
            </>
          ) : (
            <div>
              {addRows.map((row, idx) => (
                <div key={idx} className="p-3 mb-3 border rounded bg-light position-relative">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="badge bg-primary">API Endpoint #{idx + 1}</span>
                    {addRows.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm border-0 py-0 px-1"
                        onClick={() => handleRemoveRowItem(idx)}
                        title="Remove endpoint"
                      >
                        <i className="material-icons" style={{ fontSize: "18px", verticalAlign: "middle" }}>
                          delete
                        </i>
                      </button>
                    )}
                  </div>
                  <div className="row g-2">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold small">API Name <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={row.apiName}
                        onChange={(e) => handleAddRowChange(idx, "apiName", e.target.value)}
                        placeholder="e.g. OrderBooking"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold small">HTTP Method</label>
                      <select
                        className="form-select form-select-sm"
                        value={row.httpMethod}
                        onChange={(e) => handleAddRowChange(idx, "httpMethod", e.target.value)}
                      >
                        {HTTP_METHODS.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label fw-semibold small">Context / Endpoint URL <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={row.contextUrl}
                        onChange={(e) => handleAddRowChange(idx, "contextUrl", e.target.value)}
                        placeholder="e.g. /api/IntegrationAPI/OrderBooking"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="btn btn-outline-primary btn-sm rounded-pill mt-1"
                onClick={handleAddRowItem}
              >
                <i className="material-icons" style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "4px" }}>
                  add
                </i>
                Add Another API
              </button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsRowModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveRow}>
            {editingRow ? "Update" : addRows.length > 1 ? `Save All (${addRows.length})` : "Add"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Confirm Modal ── */}
      <Modal show={isDeleteModalOpen} onHide={() => setIsDeleteModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete API Endpoint</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{deleteTarget?.apiName}</strong>?
          This will also delete the associated request body.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Body Editor Modal ── */}
      {isBodyModalOpen && (
        <ViewOrUpdateApiBody
          isOpen={isBodyModalOpen}
          onRequestClose={() => setIsBodyModalOpen(false)}
          apiId={bodyModalApiId}
          apiName={bodyModalApiName}
        />
      )}
    </div>
  );
}

export default ApiUrlSettings;
